/**
 * Tuỳ chọn phương thức in + helper gửi lệnh in từ anywhere (kể cả ngoài
 * React tree).
 *
 * Persist: localStorage per browser/máy (không sync theo user/tenant).
 * Lý do: máy quầy POS thường có agent + máy in thật, còn manager mở từ
 * laptop/home thì cần dùng browser print dialog.
 *
 * Mỗi loại máy in (receipt vs label) có setting riêng vì quầy có thể có
 * máy in nhiệt LAN nhưng chưa có máy in tem.
 *
 * **Hook caller** (component): dùng `usePrint()` từ `@/hooks/useHardware`
 * — lợi thế là có loading state qua react-query mutation.
 *
 * **Non-hook caller** (util / event listener / cron): dùng `printAuto()`
 * trong file này — tự đọc setting + route + trả Promise.
 */

export type PrintMethod = 'agent' | 'browser';
export type PrintKind = 'receipt' | 'invoice' | 'gold_guarantee' | 'label';

const STORAGE_KEY = (kind: PrintKind) => `erp_print_method_${kind}`;

/**
 * Default theo loại — receipt + label thường có máy in chuyên dụng (thermal +
 * label printer) → agent. Invoice (VAT A4/A5) + gold_guarantee thường in
 * laser/preprinted → browser dialog phù hợp hơn (user tự chọn máy in trong
 * dialog hệ thống). User vẫn có thể override.
 */
const DEFAULT: Record<PrintKind, PrintMethod> = {
  receipt: 'agent',
  invoice: 'browser',
  gold_guarantee: 'browser',
  label: 'agent',
};

export function getPrintMethod(kind: PrintKind): PrintMethod {
  try {
    const v = localStorage.getItem(STORAGE_KEY(kind));
    if (v === 'agent' || v === 'browser') return v;
  } catch {
    // ignore
  }
  return DEFAULT[kind];
}

export function setPrintMethod(kind: PrintKind, method: PrintMethod): void {
  try {
    localStorage.setItem(STORAGE_KEY(kind), method);
  } catch {
    // ignore — quota exceeded / privacy mode
  }
}

/**
 * Render HTML cho browser print fallback. Tối thiểu (text-only, monospace
 * giả lập kim 80mm). Caller có thể truyền `customHtml` nếu cần preview đẹp.
 *
 * Public — dùng nội bộ trong `usePrint()` hook và `printAuto()` helper.
 */
export function printViaBrowser(opts: {
  title?: string;
  bodyHtml: string;
  widthMm?: number;
}): Promise<{ ok: true }> {
  const width = opts.widthMm ?? 80;
  const title = opts.title ?? 'Receipt';
  const fullHtml = `<!doctype html>
<html><head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: ${width}mm auto; margin: 4mm; }
  body { font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 12px;
         line-height: 1.4; margin: 0; padding: 0; width: ${width - 8}mm; }
  h1, h2, h3 { margin: 4px 0; }
  table { width: 100%; border-collapse: collapse; }
  td, th { padding: 1px 0; }
  .right { text-align: right; }
  .center { text-align: center; }
  hr { border: 0; border-top: 1px dashed #000; margin: 6px 0; }
</style></head><body>${opts.bodyHtml}</body></html>`;

  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    iframe.srcdoc = fullHtml;
    iframe.addEventListener('load', () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        // Để dialog browser hiện xong rồi mới remove (delay generous).
        setTimeout(() => iframe.remove(), 2000);
        resolve({ ok: true });
      }
    });
  });
}

// ---------------------------------------------------------------------------
// Non-hook print API — gọi từ anywhere (util, listener, cron, event handler).
// ---------------------------------------------------------------------------

import type { PrintPayload } from '@/services/hardware.service';

/** Re-export PrintPayload + sub-types làm public API canonical. Thay vì
 *  duplicate definition, mọi caller dùng `PrintTask` từ utils này. */
export type PrintTask = PrintPayload;
export type {
  ReceiptData,
  LabelData,
} from '@/services/hardware.service';

/**
 * Print router — gọi không cần React. Đọc setting localStorage:
 *   - 'agent' → POST raw bytes tới hardware agent qua hardwareService.print()
 *   - 'browser' → render HTML rồi window.print()
 *
 * Trả Promise. Không có loading state — caller tự manage UI nếu cần.
 *
 * Cho phép override setting bằng `forceMethod`:
 *   `printAuto(task, { forceMethod: 'browser' })` ép browser bất chấp setting.
 *
 * @example
 *   import { printAuto } from '@/utils/printSettings';
 *   await printAuto({
 *     type: 'receipt',
 *     data: { orderCode: 'ORD123', items: [...], total: 100000 },
 *     width: 80,
 *   });
 */
export async function printAuto(
  task: PrintTask,
  opts?: { forceMethod?: PrintMethod },
): Promise<{ ok: true }> {
  const kind: PrintKind = task.type === 'label' ? 'label' : 'receipt';
  const method = opts?.forceMethod ?? getPrintMethod(kind);

  if (method === 'browser') {
    return printViaBrowser({
      title: task.type === 'receipt' ? 'Hoá đơn' : 'Tem sản phẩm',
      bodyHtml: renderPrintHtml(task),
      widthMm: task.type === 'receipt' ? task.width ?? 80 : 50,
    });
  }

  // Agent path — late-import service tránh circular nếu printSettings được
  // import từ context không có axios.
  const { hardwareService } = await import('@/services/hardware.service');
  await hardwareService.print(task);
  return { ok: true };
}

/** Render HTML tối thiểu cho browser print. Reusable từ usePrint hook +
 *  printAuto helper. */
export function renderPrintHtml(task: PrintTask): string {
  const esc = (s: unknown) =>
    String(s ?? '').replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
    );

  if (task.type === 'receipt') {
    const d = task.data;
    const items = (d.items ?? [])
      .map(
        (it) =>
          `<tr><td>${esc(it.name)}</td><td class="right">${it.qty}</td><td class="right">${it.price.toLocaleString('vi-VN')}</td></tr>`,
      )
      .join('');
    return `
<div class="center">
  <h2>${esc(d.storeName ?? 'ERP Gold')}</h2>
  ${d.storeAddress ? `<div>${esc(d.storeAddress)}</div>` : ''}
  ${d.storePhone ? `<div>SĐT: ${esc(d.storePhone)}</div>` : ''}
</div>
<hr>
<div>Đơn: <strong>${esc(d.orderCode)}</strong></div>
<div>Thu ngân: ${esc(d.cashier ?? '-')}</div>
<div>Ngày: ${esc(d.date ?? '')}</div>
<hr>
<table>
  <thead><tr><th align="left">Hàng</th><th class="right">SL</th><th class="right">Giá</th></tr></thead>
  <tbody>${items}</tbody>
</table>
<hr>
<table>
  <tr><td>Tạm tính</td><td class="right">${(d.subtotal ?? 0).toLocaleString('vi-VN')}</td></tr>
  ${d.discount ? `<tr><td>Giảm giá</td><td class="right">-${d.discount.toLocaleString('vi-VN')}</td></tr>` : ''}
  <tr><td><strong>Tổng cộng</strong></td><td class="right"><strong>${(d.total ?? 0).toLocaleString('vi-VN')}</strong></td></tr>
  <tr><td>${esc(d.paymentMethod ?? '')}</td><td class="right">${(d.amountPaid ?? 0).toLocaleString('vi-VN')}</td></tr>
  ${d.change ? `<tr><td>Tiền thừa</td><td class="right">${d.change.toLocaleString('vi-VN')}</td></tr>` : ''}
</table>
${d.footer ? `<hr><div class="center">${esc(d.footer)}</div>` : ''}
`;
  }

  // label
  const d = task.data;
  return `
<div class="center">
  <strong>${esc(d.productName)}</strong>
  <div>${esc(d.sku)}</div>
  ${d.weight ? `<div>${esc(d.weight)}</div>` : ''}
  ${d.karat ? `<div>${esc(d.karat)}</div>` : ''}
  <div><strong>${esc(d.price)}</strong></div>
  ${d.barcode ? `<div style="font-family:monospace">||${esc(d.barcode)}||</div>` : ''}
</div>
`;
}
